/**
 * 后台接口封装。
 *
 * 后端 `hasAdminAccess()` 认两样东西：identity.isAdmin（由 ADMIN_IDS 决定，线上为空），
 * 或 `payload.adminToken === ADMIN_TOKEN`。线上只配了 ADMIN_TOKEN，
 * 所以每个 admin 请求都要带上令牌。
 *
 * 令牌存 sessionStorage 而不是 localStorage：关掉标签页即失效。
 * 这是一个能读写全站数据的口令，不该在磁盘上长期留存。
 */
import { callApi, type ApiResult } from "./api";

const ADMIN_TOKEN_KEY = "tengyunzi.adminToken";

export function getAdminToken(): string {
  try {
    return sessionStorage.getItem(ADMIN_TOKEN_KEY) ?? "";
  } catch {
    return "";
  }
}

export function setAdminToken(token: string): void {
  try {
    if (token) sessionStorage.setItem(ADMIN_TOKEN_KEY, token);
    else sessionStorage.removeItem(ADMIN_TOKEN_KEY);
  } catch {
    /* 隐私模式下不可用，忽略 */
  }
}

export function callAdmin<T = Record<string, unknown>>(
  action: string,
  payload: Record<string, unknown> = {},
): Promise<ApiResult<T>> {
  return callApi<T>(action, { ...payload, adminToken: getAdminToken() });
}

export type AdminStats = Record<string, number>;

export type AdminItem = {
  _id?: string;
  id?: string;
  ownerId?: string;
  name?: string;
  title?: string;
  createdAt?: string;
  reviewStatus?: string;
  reviewReasons?: string[];
  [key: string]: unknown;
};

export type AuditLog = {
  _id?: string;
  action?: string;
  ownerId?: string;
  createdAt?: string;
  ip?: string;
  ipLocation?: string;
  userAgent?: string;
  detail?: Record<string, unknown>;
  payload?: Record<string, unknown>;
};

export type FunnelStage = {
  key: string;
  label: string;
  value: number;
  rate: number;
};

export type FunnelResult = {
  ok: boolean;
  windowDays?: number;
  stages?: FunnelStage[];
  orders?: {
    total: number;
    byStatus: Record<string, number>;
    orderConversion: number;
  };
  revenue?: {
    paidCount: number;
    amount: number;
    refundCount: number;
    refundAmount: number;
    net: number;
    arpu: number;
    currency: string;
  };
  reach?: {
    distinctIps: number;
    auditEvents: number;
  };
};

export type AdminOrder = AdminItem & {
  productName?: string;
  amount?: number;
  currency?: string;
  status?: string;
};

export const REVIEW_STATUSES = ["pass", "review", "rejected"] as const;
export type ReviewStatus = (typeof REVIEW_STATUSES)[number];

export const REVIEW_LABEL: Record<string, string> = {
  pass: "通过",
  review: "待复核",
  rejected: "已拒绝",
};

export const ORDER_STATUSES = ["pending", "paid", "refunded", "cancelled"] as const;

export const ORDER_LABEL: Record<string, string> = {
  pending: "待支付",
  paid: "已支付",
  refunded: "已退款",
  cancelled: "已取消",
};
